import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Badge } from "@/components/UI";
import { Folder, FolderOpen, File as FileIcon, CaretRight, CaretDown, Buildings, TreeStructure, TagChevron, Coins, Truck } from "@phosphor-icons/react";
import FilePreview from "@/components/FilePreview";

const typeIcons = {
  filiera: TreeStructure, beneficiario: Buildings, tabella: TagChevron,
  categoria: Folder, investimento: Coins, fornitore: Truck, cartella: FolderOpen, file: FileIcon,
};

// Flatten tree into rows with level
function flatten(nodes, expanded, level = 0, out = [], parentPath = "") {
  for (const n of nodes || []) {
    const path = `${parentPath}/${n.id}`;
    const isOpen = expanded[path] !== false && (level < 2 || expanded[path] === true);
    out.push({ ...n, level, path, isOpen, hasChildren: !!(n.children && n.children.length) });
    if (isOpen && n.children) flatten(n.children, expanded, level + 1, out, path);
  }
  return out;
}

export default function DocumentCenter() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get("/document-center/tree").then((r) => { setTree(r.data); setLoading(false); });
  }, []);

  const toggle = (path, currentlyOpen) => {
    setExpanded((prev) => ({ ...prev, [path]: !currentlyOpen }));
  };

  const rows = flatten(tree, expanded);

  return (
    <div className="fade-in" data-testid="doccenter-page">
      <PageHeader eyebrow="Document Center" title="Archivio Documenti" />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="p-4 border-b border-slate-200">
            <div className="eyebrow">Struttura gerarchica</div>
            <div className="text-xs text-slate-500 mt-1">
              Filiera → Beneficiario → Tabella → Categoria → Investimento → Fornitore → Cartella → File
            </div>
          </div>
          <div className="p-3">
            {loading && <div className="p-6 text-slate-500 text-sm">Caricamento…</div>}
            {!loading && rows.length === 0 && <div className="p-6 text-slate-400 text-sm">Nessun documento archiviato</div>}
            {rows.map((n) => {
              const Icon = typeIcons[n.type] || FileIcon;
              const isFile = n.type === "file";
              const colorClass =
                n.type === "filiera" ? "text-blue-700" :
                n.type === "beneficiario" ? "text-emerald-700" :
                n.type === "investimento" ? "text-purple-700" :
                isFile ? "text-slate-500" : "text-amber-600";
              return (
                <div key={n.path} className="tree-item"
                  style={{ paddingLeft: 8 + n.level * 18 }}
                  onClick={() => {
                    if (isFile) setPreview({ id: n.id, filename: n.name, ext: n.ext, contentType: "" });
                    else if (n.hasChildren) toggle(n.path, n.isOpen);
                  }}
                  data-testid={`tree-node-${n.id}`}>
                  {isFile || !n.hasChildren ? (
                    <span className="inline-block w-3" />
                  ) : n.isOpen ? (
                    <CaretDown size={12} />
                  ) : (
                    <CaretRight size={12} />
                  )}
                  <Icon size={15} weight={isFile ? "regular" : "duotone"} className={colorClass} />
                  <span className={isFile ? "text-slate-700" : "font-medium text-slate-900"}>{n.name}</span>
                  {isFile && !n.classificato && <Badge tone="amber">nuovo</Badge>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {preview && <FilePreview {...preview} fileId={preview.id} onClose={() => setPreview(null)} />}
    </div>
  );
}
